import { Refine, Authenticated } from '@refinedev/core';
import { RefineKbar, RefineKbarProvider } from '@refinedev/kbar';
import {
  ThemedLayoutV2,
  ThemedTitleV2,
  useNotificationProvider,
  ErrorComponent,
} from '@refinedev/antd';
import routerBindings, {
  UnsavedChangesNotifier,
  DocumentTitleHandler,
  CatchAllNavigate,
} from '@refinedev/react-router-v6';
import { BrowserRouter, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { App as AntdApp, ConfigProvider, theme } from 'antd';
import {
  UserOutlined,
  BarChartOutlined,
  SettingOutlined,
  BellOutlined,
  TrophyOutlined,
  TeamOutlined,
  ApiOutlined,
  ThunderboltOutlined,
  GiftOutlined,
  ShopOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  LinkOutlined,
  FireOutlined,
  MailOutlined,
  AimOutlined,
  PictureOutlined,
  RiseOutlined,
  FlagOutlined,
  CalendarOutlined,
  FundOutlined,
  LineChartOutlined,
  FunnelPlotOutlined,
  DashboardOutlined,
  ExperimentOutlined,
  SafetyOutlined,
  FieldTimeOutlined,
  WarningOutlined,
  SearchOutlined,
} from '@ant-design/icons';

import { dataProvider } from './providers/dataProvider';
import { authProvider } from './providers/authProvider';
import { accessControlProvider } from './providers/accessControlProvider';
import { GroupedSider } from './components/layout/GroupedSider';

// Pages
import { LoginPage } from './pages/auth/login';
import { TodayPage } from './pages/today';
import { PlayerList, PlayerShow } from './pages/players';
import { AnalyticsPage } from './pages/analytics';
import { RisksPage } from './pages/risks';
import { LeaderboardOpensPage } from './pages/leaderboards';
import { BqInsightsPage } from './pages/bq-insights';
import { FtueProgressionPage } from './pages/ftue-progression';
import { ProductMetricsPage } from './pages/product-metrics';
import { MonetizationPage } from './pages/monetization';
import { ConfigPage } from './pages/config';
import { LiveOpsPage } from './pages/liveops';
import { CricketCalendarPage } from './pages/cricket-calendar';
import { MatchPushesPage } from './pages/match-pushes';
import { TournamentList, TournamentCreate } from './pages/tournaments';
import { WebhooksPage } from './pages/webhooks';
import { OfferList, OfferCreate, OfferEdit, OfferShow } from './pages/offers';
import {
  ProductList,
  ProductCreate,
  ProductEdit,
  ProductShow,
  SubscriptionList,
} from './pages/products';
import { PurchaseList, PurchaseShow } from './pages/purchases';
import { CronJobsPage } from './pages/cron-jobs';
import { DiagnosticsPage } from './pages/diagnostics';
import { MyPermissionsPage } from './pages/profile/permissions';

// New module pages
import StoreSyncPage from './pages/store-sync';
import DeeplinksPage from './pages/deeplinks';
import HotEventsPage from './pages/hot-events';
import BrandingPage from './pages/branding';
import InboxPage from './pages/inbox';
import MissionsPage from './pages/missions';
import CohortsPage from './pages/cohorts';
import { CustomAdsPage } from './pages/custom-ads';
import { ExperimentsPage } from './pages/experiments';
import { ExperimentsV2Page } from './pages/experiments-v2';
import { EditorPage as ExperimentsV2Editor } from './pages/experiments-v2/EditorPage';
import { DetailPage as ExperimentsV2Detail } from './pages/experiments-v2/DetailPage';
import { DailyDealsPage } from './pages/daily-deals';
import { DailyChallengePage } from './pages/daily-challenge';
import { DailyRewardsPage } from './pages/daily-rewards';
import { MatchdayDirectorPage } from './pages/matchday-director';
import { PvpLivePage } from './pages/pvp-live';
import { PvpMatchesPage } from './pages/pvp-matches';
import { PvpReviewPage } from './pages/pvp-review';
import { PvpSyncPage } from './pages/pvp-sync';
import CmNationalTeamsPage from './pages/cm-national-teams';
import AdminsPage from './pages/admins';
import AdminShowPage from './pages/admins/show';
import {
  PlayerDataList,
  PlayerDataCreate,
  PlayerDataEdit,
  PlayerDataShow,
} from './pages/player-data';

// AI Analytics
import { AiAnalyticsPage } from './pages/ai-analytics';

// Components
import { Header } from './components/layout/Header';

import '@refinedev/antd/dist/reset.css';
import LiveEventList from './pages/live-events';
import LiveEventForm from './pages/live-events/form';
import LiveEventShow from './pages/live-events/show';

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <RefineKbarProvider>
        <ConfigProvider
          theme={{
            algorithm: theme.defaultAlgorithm,
            token: {
              colorPrimary: '#1890ff',
              borderRadius: 6,
            },
          }}
        >
          <AntdApp>
            <Refine
              dataProvider={dataProvider}
              authProvider={authProvider}
              accessControlProvider={accessControlProvider}
              routerProvider={routerBindings}
              notificationProvider={useNotificationProvider}
              resources={[
                // ---- Daily watch (high-frequency dashboards & operations) ----
                {
                  name: 'analytics',
                  identifier: 'today',
                  list: '/today',
                  meta: {
                    label: 'Today',
                    icon: <ThunderboltOutlined />,
                  },
                },
                {
                  name: 'analytics',
                  identifier: 'monetization',
                  list: '/monetization',
                  meta: {
                    label: 'Monetization',
                    icon: <LineChartOutlined />,
                  },
                },
                {
                  name: 'analytics',
                  list: '/analytics',
                  meta: {
                    label: 'Analytics',
                    icon: <BarChartOutlined />,
                  },
                },
                {
                  // Same permission as Analytics (analytics:list on the API).
                  name: 'analytics',
                  identifier: 'risks',
                  list: '/risks',
                  meta: {
                    label: 'Risks & Hacks',
                    icon: <WarningOutlined />,
                  },
                },
                {
                  // Same permission as Analytics (analytics:list on the API).
                  name: 'analytics',
                  identifier: 'leaderboard-opens',
                  list: '/leaderboard-opens',
                  meta: {
                    label: 'Leaderboard Opens',
                    icon: <TrophyOutlined />,
                  },
                },
                {
                  name: 'analytics',
                  identifier: 'bq-insights',
                  list: '/bq-insights',
                  meta: {
                    label: 'BigQuery Insights',
                    icon: <FundOutlined />,
                  },
                },
                {
                  name: 'analytics',
                  identifier: 'product-metrics',
                  list: '/product-metrics',
                  meta: {
                    label: 'Product Metrics',
                    icon: <DashboardOutlined />,
                  },
                },
                {
                  name: 'analytics',
                  identifier: 'ftue-progression',
                  list: '/ftue-progression',
                  meta: {
                    label: 'FTUE & Progression',
                    icon: <FunnelPlotOutlined />,
                  },
                },
                {
                  name: 'ai-analytics',
                  list: '/ai-analytics',
                  meta: {
                    label: 'AI Analytics',
                    icon: <RiseOutlined />,
                  },
                },
                {
                  name: 'players',
                  list: '/players',
                  show: '/players/:id',
                  meta: {
                    label: 'Players',
                    icon: <UserOutlined />,
                  },
                },
                {
                  name: 'purchases',
                  list: '/purchases',
                  show: '/purchases/:id',
                  meta: {
                    label: 'Purchases',
                    icon: <DollarOutlined />,
                  },
                },

                // ---- LiveOps content (campaigns, events, engagement) ----
                {
                  name: 'live-events',
                  list: '/live-events',
                  show: '/live-events/:id',
                  create: '/live-events/create',
                  edit: '/live-events/:id/edit',
                  meta: {
                    label: 'Live Events',
                    icon: <ThunderboltOutlined />,
                  },
                },
                {
                  name: 'tournaments',
                  list: '/tournaments',
                  create: '/tournaments/create',
                  show: '/tournaments/:id',
                  meta: {
                    label: 'Tournaments',
                    icon: <TrophyOutlined />,
                  },
                },
                {
                  name: 'daily-deals',
                  list: '/daily-deals',
                  meta: {
                    label: 'Daily Deals',
                    icon: <GiftOutlined />,
                  },
                },
                {
                  // Same backend permission resource as Configuration
                  // (config:list in the sidebar, config:show / config:edit
                  // on the API); the identifier keeps it a separate entry.
                  name: 'config',
                  identifier: 'daily-rewards',
                  list: '/daily-rewards',
                  meta: {
                    label: 'Daily Rewards',
                    icon: <CalendarOutlined />,
                  },
                },
                {
                  name: 'hot-events',
                  list: '/hot-events',
                  meta: {
                    label: 'Hot Events',
                    icon: <FireOutlined />,
                  },
                },
                {
                  name: 'branding',
                  list: '/branding',
                  meta: {
                    label: 'Branding',
                    icon: <PictureOutlined />,
                  },
                },
                {
                  name: 'missions',
                  list: '/missions',
                  meta: {
                    label: 'Missions',
                    icon: <AimOutlined />,
                  },
                },
                {
                  name: 'offers',
                  list: '/offers',
                  create: '/offers/create',
                  edit: '/offers/:id/edit',
                  show: '/offers/:id',
                  meta: {
                    label: 'Offers',
                    icon: <GiftOutlined />,
                  },
                },
                {
                  name: 'custom-ads',
                  list: '/custom-ads',
                  meta: {
                    label: 'Custom Ads',
                    icon: <PictureOutlined />,
                  },
                },
                {
                  name: 'experiments',
                  list: '/experiments',
                  meta: {
                    label: 'Experiments',
                    icon: <ExperimentOutlined />,
                  },
                },
                {
                  name: 'experiments',
                  identifier: 'experiments-v2',
                  list: '/experiments-v2',
                  meta: {
                    label: 'Experiments v2',
                    icon: <ExperimentOutlined />,
                  },
                },

                // ---- Player-facing comms ----
                {
                  name: 'inbox',
                  list: '/inbox',
                  meta: {
                    label: 'Inbox',
                    icon: <MailOutlined />,
                  },
                },
                {
                  name: 'liveops',
                  list: '/liveops',
                  meta: {
                    label: 'Notifications',
                    icon: <BellOutlined />,
                  },
                },
                {
                  // Same permission as the backend routes (liveops:*).
                  name: 'liveops',
                  identifier: 'daily-challenge',
                  list: '/daily-challenge',
                  meta: {
                    label: 'Daily Challenge',
                    icon: <FireOutlined />,
                  },
                },
                {
                  name: 'cricket-calendar',
                  list: '/cricket-calendar',
                  meta: {
                    label: 'Cricket Calendar',
                    icon: <CalendarOutlined />,
                  },
                },
                {
                  name: 'match-pushes',
                  list: '/match-pushes',
                  meta: {
                    label: 'Match Pushes',
                    icon: <BellOutlined />,
                  },
                },
                {
                  name: 'cohorts',
                  list: '/cohorts',
                  meta: {
                    label: 'Cohorts',
                    icon: <TeamOutlined />,
                  },
                },

                // ---- Game data (configuration of what players see) ----
                {
                  name: 'cm-national-teams',
                  list: '/cm-national-teams',
                  meta: {
                    label: 'CM National Teams',
                    icon: <FlagOutlined />,
                  },
                },
                {
                  name: 'player-data',
                  list: '/player-data',
                  create: '/player-data/create',
                  edit: '/player-data/:id/edit',
                  show: '/player-data/:id',
                  meta: {
                    label: 'Player Data',
                    icon: <TeamOutlined />,
                  },
                },
                {
                  name: 'products',
                  list: '/products',
                  create: '/products/create',
                  edit: '/products/:id/edit',
                  show: '/products/:id',
                  meta: {
                    label: 'Products',
                    icon: <ShopOutlined />,
                  },
                },
                {
                  name: 'store-sync',
                  list: '/store-sync',
                  meta: {
                    label: 'Store Sync',
                    icon: <SyncOutlined />,
                  },
                },

                // ---- System / integrations (rare touch) ----
                {
                  name: 'config',
                  list: '/config',
                  meta: {
                    label: 'Configuration',
                    icon: <SettingOutlined />,
                  },
                },
                {
                  // Same permission as PvP Review (players:show on the API).
                  name: 'players',
                  identifier: 'pvp-live',
                  list: '/pvp-live',
                  meta: {
                    label: 'PvP Live',
                    icon: <DashboardOutlined />,
                  },
                },
                {
                  // Same permission as PvP Review (players:show on the API).
                  name: 'players',
                  identifier: 'pvp-matches',
                  list: '/pvp-matches',
                  meta: {
                    label: 'PvP Matches',
                    icon: <SearchOutlined />,
                  },
                },
                {
                  // Same backend permission resource as Players (players:list
                  // in the sidebar, players:show / players:edit on the API);
                  // the identifier keeps it a separate entry. Its settings are
                  // the Realtime PvP tab of Configuration.
                  name: 'players',
                  identifier: 'pvp-review',
                  list: '/pvp-review',
                  meta: {
                    label: 'PvP Review',
                    icon: <SafetyOutlined />,
                  },
                },
                {
                  // Same permission as PvP Review (players:show on the API).
                  name: 'players',
                  identifier: 'pvp-sync',
                  list: '/pvp-sync',
                  meta: {
                    label: 'PvP Sync',
                    icon: <FieldTimeOutlined />,
                  },
                },
                {
                  name: 'matchday-director',
                  list: '/matchday-director',
                  meta: {
                    label: 'Matchday-Director',
                    icon: <AimOutlined />,
                  },
                },
                {
                  name: 'deeplinks',
                  list: '/deeplinks',
                  meta: {
                    label: 'Deeplinks',
                    icon: <LinkOutlined />,
                  },
                },
                {
                  name: 'webhooks',
                  list: '/webhooks',
                  meta: {
                    label: 'Webhooks',
                    icon: <ApiOutlined />,
                  },
                },
                {
                  name: 'cron-jobs',
                  list: '/cron-jobs',
                  meta: {
                    label: 'Cron Jobs',
                    icon: <ClockCircleOutlined />,
                  },
                },
                {
                  name: 'diagnostics',
                  list: '/diagnostics',
                  meta: {
                    label: 'Diagnostics',
                    icon: <ApiOutlined />,
                  },
                },

                // ---- Administration (sensitive, kept last) ----
                {
                  name: 'admins',
                  list: '/admins',
                  show: '/admins/:id',
                  meta: {
                    label: 'Admin Users',
                    icon: <TeamOutlined />,
                  },
                },
              ]}
              options={{
                syncWithLocation: true,
                warnWhenUnsavedChanges: true,
                useNewQueryKeys: true,
              }}
            >
              <Routes>
                {/* Public routes */}
                <Route
                  element={
                    <Authenticated key="auth" fallback={<Outlet />}>
                      <Navigate to="/today" replace />
                    </Authenticated>
                  }
                >
                  <Route path="/login" element={<LoginPage />} />
                </Route>

                {/* Protected routes */}
                <Route
                  element={
                    <Authenticated key="protected" fallback={<CatchAllNavigate to="/login" />}>
                      <ThemedLayoutV2
                        Header={() => <Header />}
                        Sider={() => (
                          <GroupedSider
                            Title={({ collapsed }) => (
                              <ThemedTitleV2 collapsed={collapsed} text="WCC4 Admin" />
                            )}
                          />
                        )}
                      >
                        <Outlet />
                      </ThemedLayoutV2>
                    </Authenticated>
                  }
                >
                  {/* Today is the landing page; the Dashboard was removed 26 Oct 2026. */}
                  <Route index element={<Navigate to="/today" replace />} />
                  <Route path="/profile/permissions" element={<MyPermissionsPage />} />
                  <Route path="/players">
                    <Route index element={<PlayerList />} />
                    <Route path=":id" element={<PlayerShow />} />
                  </Route>
                  <Route path="/today" element={<TodayPage />} />
                  <Route path="/monetization" element={<MonetizationPage />} />
                  <Route path="/analytics" element={<AnalyticsPage />} />
                  <Route path="/risks" element={<RisksPage />} />
                  <Route path="/leaderboard-opens" element={<LeaderboardOpensPage />} />
                  <Route path="/bq-insights" element={<BqInsightsPage />} />
                  <Route path="/product-metrics" element={<ProductMetricsPage />} />
                  <Route path="/ftue-progression" element={<FtueProgressionPage />} />
                  <Route path="/ai-analytics" element={<AiAnalyticsPage />} />
                  <Route path="/liveops" element={<LiveOpsPage />} />
                  <Route path="/cricket-calendar" element={<CricketCalendarPage />} />
                  <Route path="/match-pushes" element={<MatchPushesPage />} />
                  <Route path="/offers">
                    <Route index element={<OfferList />} />
                    <Route path="create" element={<OfferCreate />} />
                    <Route path=":id" element={<OfferShow />} />
                    <Route path=":id/edit" element={<OfferEdit />} />
                  </Route>
                  <Route path="/products">
                    <Route index element={<ProductList />} />
                    <Route path="create" element={<ProductCreate />} />
                    <Route path=":id" element={<ProductShow />} />
                    <Route path=":id/edit" element={<ProductEdit />} />
                    <Route path="subscriptions" element={<SubscriptionList />} />
                  </Route>
                  <Route path="/purchases">
                    <Route index element={<PurchaseList />} />
                    <Route path=":id" element={<PurchaseShow />} />
                  </Route>
                  <Route path="/tournaments">
                    <Route index element={<TournamentList />} />
                    <Route path="create" element={<TournamentCreate />} />
                  </Route>
                  <Route path="/live-events">
                    <Route index element={<LiveEventList />} />
                    <Route path="create" element={<LiveEventForm />} />
                    <Route path=":id" element={<LiveEventShow />} />
                    <Route path=":id/edit" element={<LiveEventForm />} />
                  </Route>
                  <Route path="/config" element={<ConfigPage />} />
                  <Route path="/matchday-director" element={<MatchdayDirectorPage />} />
                  <Route path="/pvp-live" element={<PvpLivePage />} />
                  <Route path="/pvp-matches" element={<PvpMatchesPage />} />
                  <Route path="/pvp-review" element={<PvpReviewPage />} />
                  <Route path="/pvp-sync" element={<PvpSyncPage />} />
                  <Route path="/webhooks" element={<WebhooksPage />} />
                  <Route path="/player-data">
                    <Route index element={<PlayerDataList />} />
                    <Route path="create" element={<PlayerDataCreate />} />
                    <Route path=":id" element={<PlayerDataShow />} />
                    <Route path=":id/edit" element={<PlayerDataEdit />} />
                  </Route>
                  <Route path="/cron-jobs" element={<CronJobsPage />} />
                  <Route path="/diagnostics" element={<DiagnosticsPage />} />
                  <Route path="/deeplinks" element={<DeeplinksPage />} />
                  <Route path="/hot-events" element={<HotEventsPage />} />
                  <Route path="/branding" element={<BrandingPage />} />
                  <Route path="/inbox" element={<InboxPage />} />
                  <Route path="/missions" element={<MissionsPage />} />
                  <Route path="/cohorts" element={<CohortsPage />} />
                  <Route path="/cohorts" element={<CohortsPage />} />
                  <Route path="/store-sync" element={<StoreSyncPage />} />
                  <Route path="/custom-ads" element={<CustomAdsPage />} />
                  <Route path="/experiments" element={<ExperimentsPage />} />
                  <Route path="/experiments-v2" element={<ExperimentsV2Page />} />
                  <Route path="/experiments-v2/new" element={<ExperimentsV2Editor />} />
                  <Route path="/experiments-v2/:key/edit" element={<ExperimentsV2Editor />} />
                  <Route path="/experiments-v2/:key" element={<ExperimentsV2Detail />} />
                  <Route path="/daily-deals" element={<DailyDealsPage />} />
                  <Route path="/daily-challenge" element={<DailyChallengePage />} />
                  <Route path="/daily-rewards" element={<DailyRewardsPage />} />
                  <Route path="/cm-national-teams" element={<CmNationalTeamsPage />} />
                  <Route path="/admins" element={<AdminsPage />} />
                  <Route path="/admins/:id" element={<AdminShowPage />} />
                  <Route path="*" element={<ErrorComponent />} />
                </Route>
              </Routes>

              <RefineKbar />
              <UnsavedChangesNotifier />
              <DocumentTitleHandler />
            </Refine>
          </AntdApp>
        </ConfigProvider>
      </RefineKbarProvider>
    </BrowserRouter>
  );
};

export default App;
